/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselProvider } from '@/components/blocks/Carousel/CarouselProvider';
import { CarouselSlide } from '@/components/blocks/Carousel/CarouselSlide';
import { IconButtonBack, IconButtonNext } from '@/components/blocks/Carousel/CarouselSlider';
import { carouselProviderSX } from '@/components/blocks/Carousel/styles/carouselProvider';
import { carouselSlideSX } from '@/components/blocks/Carousel/styles/carouselSlide';
import { CarouselOptions } from '@/data/types/Carousel';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material';
import { DotGroup, Slider } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { AriaAttributes, useMemo } from 'react';
import './navigationStyles.css';

type CarouselSliderProps = {
	a11yProps?: AriaAttributes[];
	slides: JSX.Element[];
	carouselProps?: CarouselOptions;
	carouselSlideStyles?: SxProps<Theme>;
	showNavigationCustom?: boolean;
	showPaginationCustomComponent?: boolean;
	customPaginationStyles?: React.CSSProperties;
};

const defaultCarouselProps = {
	naturalSlideWidth: 248,
	naturalSlideHeight: 300,
	visibleSlides: 1,
	step: 1,
	infinite: true,
	dragEnabled: true,
	totalSlides: 1,
	isIntrinsicHeight: true,
	touchEnabled: true,
};

const EMPTY_A11_PROPS: AriaAttributes[] = [];
export const CarouselSlider = ({
	a11yProps = EMPTY_A11_PROPS,
	slides,
	carouselProps,
	carouselSlideStyles = carouselSlideSX,
	showNavigationCustom = false,
	showPaginationCustomComponent = false,
	customPaginationStyles,
}: CarouselSliderProps) => {
	const mergedProps = { ...defaultCarouselProps, ...carouselProps };
	const renderNavigationCustomControl = showNavigationCustom && slides.length > 1;

	const slideElements = useMemo(
		() =>
			slides.map((slide: JSX.Element, i: number) => (
				<CarouselSlide
					index={i}
					key={slide.key}
					sx={carouselSlideStyles}
					id={`carousel-slide-index-${i}-${slide.key}`}
					data-testid={`carousel-slide-index-${i}-${slide.key}`}
					{...(a11yProps[i] ?? {})}
				>
					{slide}
				</CarouselSlide>
			)),
		[slides, carouselSlideStyles, a11yProps]
	);

	const renderNavigationKeysByDefault = () => (
		<>
			<IconButtonBack alpha>
				<ChevronLeft />
			</IconButtonBack>
			<IconButtonNext alpha>
				<ChevronRight />
			</IconButtonNext>
		</>
	);

	return (
		<CarouselProvider {...mergedProps} totalSlides={slides.length} sx={carouselProviderSX}>
			<Slider>{slideElements}</Slider>
			{renderNavigationCustomControl ? renderNavigationKeysByDefault() : null}
			{showPaginationCustomComponent ? (
				<div className="custom-dot-group" style={customPaginationStyles}>
					<DotGroup />
				</div>
			) : null}
		</CarouselProvider>
	);
};
